import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useNavigate } from 'react-router-dom';

const services = [
  {
    slug: 'lgs',
    icon: 'ri-layout-grid-line',
    title: '軽鉄下地LGS工事',
    desc: '近年RC造・S造の建築物において、天井・壁の下地として一般的に使用されている軽量鉄下地です。木下地に比べて耐火性能の向上や施工期間の短縮などの利点があります。各メーカーの耐震天井工法や難易度の高いR仕様などにも対応可能です。',
    image: 'https://public.readdy.ai/ai/img_res/eb895564ae117ae7700346a9733fb6b6.jpg',
  },
  {
    slug: 'board',
    icon: 'ri-layout-masonry-line',
    title: 'ボード工事',
    desc: '石膏ボード・岩綿ジプトーン・化粧板などの各種内装用ボード張り施工を行います。耐火性・遮音性・意匠性を兼ね備えた高品質な内装仕上げを提供します。',
    image: 'https://readdy.ai/api/search-image?query=professional%20drywall%20gypsum%20board%20installation%20interior%20construction%20workers%20installing%20plasterboard%20ceiling%20wall%20panels%20modern%20building%20interior%20renovation%20skilled%20craftsmen%20tools%20bright%20construction%20site%20clean%20workspace%20Japanese%20commercial%20building%20interior%20finishing%20work&width=600&height=400&seq=service-board-001&orientation=landscape',
  },
  {
    slug: 'wallpaper',
    icon: 'ri-paint-brush-line',
    title: '壁紙クロス工事',
    desc: '住宅・マンション・オフィス・商業施設など、あらゆる空間の壁面・天井面に壁紙（クロス）を張る内装仕上工事です。豊富な素材と熟練の職人技術で美しい空間を実現します。',
    image: 'https://readdy.ai/api/search-image?query=professional%20wallpaper%20installation%20interior%20design%20elegant%20Japanese%20room%20beautiful%20patterned%20wallpaper%20skilled%20craftsman%20interior%20renovation%20high%20quality%20wall%20covering%20installation%20clean%20bright%20modern%20room%20premium%20wallpaper%20texture%20warm%20lighting%20sophisticated%20residential%20interior&width=600&height=400&seq=service-wallpaper-001&orientation=landscape',
  },
  {
    slug: 'floor',
    icon: 'ri-layout-bottom-line',
    title: '床仕上工事',
    desc: 'フローリング・タイル・カーペット・長尺シートなど各種床材の施工を行います。住宅から商業施設・医療福祉施設まで、美しく耐久性の高い床仕上げを提供します。',
    image: 'https://readdy.ai/api/search-image?query=Chinese%20male%20worker%20installing%20hardwood%20floor%20tiles%20indoor%20construction%20site%20Asian%20craftsman%20kneeling%20laying%20flooring%20materials%20professional%20interior%20renovation%20worker%20wearing%20work%20clothes%20safety%20gear%20Chinese%20construction%20team%20floor%20finishing%20work%20bright%20clean%20room&width=600&height=400&seq=service-floor-cn-2025&orientation=landscape',
  },
];

export default function Services() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();

  return (
    <section id="services" className="py-16 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className="text-center mb-12 md:mb-16"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(32px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
          }}
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-8 h-px bg-[#B8960C]"></div>
            <span className="text-[#B8960C] text-xs tracking-[0.3em] font-medium uppercase">Our Services</span>
            <div className="w-8 h-px bg-[#B8960C]"></div>
          </div>
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1A1A] mb-4"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            事業内容
          </h2>
          <p className="text-gray-500 text-sm md:text-base max-w-2xl mx-auto leading-relaxed px-4">
            株式会社永恒は、内装仕上工事を専門とし、軽鉄下地LGS工事・ボード工事・壁紙クロス工事・床仕上工事を<br className="hidden md:block" />
            高い技術力と豊富な実績でご提供します。
          </p>
        </div>

        {/* Grid — 4 cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {services.map((s, i) => (
            <ServiceCard key={s.slug} service={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

interface Service {
  slug: string;
  icon: string;
  title: string;
  desc: string;
  image: string;
}

function ServiceCard({ service: s, index: i }: { service: Service; index: number }) {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  const navigate = useNavigate();

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      onClick={() => navigate(`/services/${s.slug}`)}
      className="group bg-white border border-gray-100 rounded-sm overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
        transition: `opacity 0.6s ease ${i * 0.12}s, transform 0.6s ease ${i * 0.12}s, box-shadow 0.3s ease`,
      }}
    >
      <div className="w-full h-44 md:h-48 overflow-hidden">
        <img
          src={s.image}
          alt={s.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-4 md:p-5">
        <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-[#B8960C]/10 rounded-md mb-3 md:mb-4">
          <i className={`${s.icon} text-lg md:text-xl text-[#B8960C]`}></i>
        </div>
        <h3
          className="text-sm md:text-base font-bold text-[#1A1A1A] mb-2 md:mb-3 tracking-wide"
          style={{ fontFamily: "'Noto Serif JP', serif" }}
        >
          {s.title}
        </h3>
        <p className="text-gray-500 text-sm md:text-base leading-relaxed mb-4 md:mb-5 line-clamp-4">{s.desc}</p>
        <div className="inline-flex items-center gap-2 text-[#B8960C] text-xs md:text-sm font-medium group-hover:gap-3 transition-all whitespace-nowrap">
          詳しく見る
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-line"></i>
          </div>
        </div>
      </div>
    </div>
  );
}